import React, { useMemo, useState } from "react";
import CopyIcon from "@atlaskit/icon/core/copy";
import { duplicateWebAction } from "../../../../../common/factory/action.factory";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setEditorSaved, saveFlow } from "../../../../../redux/actions/integrationActions";
import { Checkbox } from "@atlaskit/checkbox";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from "@atlaskit/modal-dialog";

import Button from "@atlaskit/button/new";
import Tooltip from "@atlaskit/tooltip";

const DuplicateActionMenuItem = ({ action, onClickCallback, iconOnly = false }) => {
  const flow = useSelector((state) => state.integration.flow);
  const [isShowDuplicateModal, setShowDuplicateModal] = useState(false);
  const [copyChildren, setCopyChildren] = useState(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const findChildrenForActionId = (id, actions) => {
    return actions.filter((a) => a?.parentId === id);
  };

  const findDescendantsForActionId = (id, actions) => {
    const descendants = [];
    const stack = findChildrenForActionId(id, actions);

    while (stack.length > 0) {
      const child = stack.shift();
      descendants.push(child);
      stack.push(...findChildrenForActionId(child.id, actions));
    }

    return descendants;
  };

  const childActions = useMemo(() => {
    const flowActions = Array.isArray(flow?.actions) ? flow.actions : [];
    if (!action?.id) {
      return [];
    }
    return findDescendantsForActionId(action.id, flowActions);
  }, [action?.id, flow?.actions]);

  const doDuplicate = (includeChildren) => {
    if (!action?.id) {
      return;
    }

    const flowActions = Array.isArray(flow?.actions) ? flow.actions : [];
    const descendants = includeChildren ? childActions : [];
    const actionsToDuplicate = [action, ...descendants].filter(Boolean);
    const actionIdToDuplicateId = {};
    const duplicatedActions = actionsToDuplicate.map((currentAction) => {
      const duplicate = duplicateWebAction(currentAction);
      actionIdToDuplicateId[currentAction.id] = duplicate.id;
      return duplicate;
    });

    const remappedDuplicatedActions = duplicatedActions.map((duplicatedAction, index) => {
      const originalAction = actionsToDuplicate[index];
      const duplicatedParentId = actionIdToDuplicateId[originalAction.parentId];
      return {
        ...duplicatedAction,
        parentId: duplicatedParentId || originalAction.parentId,
      };
    });

    flow.actions = [...flowActions, ...remappedDuplicatedActions];

    dispatch(setEditorSaved(true));
    dispatch(saveFlow(flow));
    navigate("/integration/" + flow.id + "/action/" + remappedDuplicatedActions[0].id);
    if (typeof onClickCallback === "function") {
      onClickCallback();
    }
  };

  const handleDuplicateClick = () => {
    if (!action?.id) {
      return;
    }

    if (childActions.length === 0) {
      doDuplicate(false);
      return;
    }
    setCopyChildren(true);
    setShowDuplicateModal(true);
    if (typeof onClickCallback === "function") {
      onClickCallback();
    }
  };

  const onDuplicateConfirm = () => {
    setShowDuplicateModal(false);
    doDuplicate(copyChildren);
  };

  const onDuplicateModalClose = () => {
    setShowDuplicateModal(false);
  };

  const duplicateButton = iconOnly ? (
    <Tooltip position="top" content="Duplicate action">
      <Button iconBefore={CopyIcon} appearance="subtle" onClick={handleDuplicateClick} aria-label="Duplicate action" />
    </Tooltip>
  ) : (
    <Button iconBefore={CopyIcon} appearance="subtle" onClick={handleDuplicateClick}>
      Duplicate action
    </Button>
  );

  const duplicateModal = (
    <ModalTransition>
      {isShowDuplicateModal && (
        <Modal onClose={onDuplicateModalClose} width={600}>
          <ModalHeader>
            <ModalTitle>Duplicate Action</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>
              <b>{action?.name || "Selected action"}</b> has {childActions.length} child action{childActions.length === 1 ? "" : "s"}.
            </p>
            <Checkbox
              label={`Copy all child actions (${childActions.length})`}
              isChecked={copyChildren}
              onChange={(event) => setCopyChildren(event.target.checked)}
            />
          </ModalBody>
          <ModalFooter>
            <Button appearance="subtle" onClick={onDuplicateModalClose}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={onDuplicateConfirm}>
              Duplicate
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );

  if (iconOnly) {
    return (
      <>
        {duplicateButton}
        {duplicateModal}
      </>
    );
  }

  return (
    <>
      {duplicateButton}
      {duplicateModal}
    </>
  );
};
export default DuplicateActionMenuItem;
